import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, serverTimestamp, resumo } from './firestore-compat';

const PAD_DISCIPLINES = [
  { key: 'fisioterapia',         label: 'Fisioterapia' },
  { key: 'fonoaudiologia',       label: 'Fonoaudiologia' },
  { key: 'nutricao',             label: 'Nutrição' },
  { key: 'tecEnfermagem',        label: 'Téc. Enfermagem' },
  { key: 'enfermeiro',           label: 'Enfermeiro' },
  { key: 'medico',               label: 'Médico' },
  { key: 'medicoEspecialista',   label: 'Médico Especialista' },
  { key: 'psicologo',            label: 'Psicólogo' },
  { key: 'terapeutaOcupacional', label: 'Terapeuta Ocupacional' },
];

const C = {
  primary: '#6366f1', text: '#1e293b', muted: '#64748b',
  border: '#e2e8f0', bg: '#f8fafc', white: '#fff',
  green: '#22c55e', greenBg: '#f0fdf4', greenBorder: '#bbf7d0',
};

function fmtDate(s) {
  if (!s) return '—';
  const d = s.includes('-') ? s.split('-').reverse().join('/') : s;
  return d;
}

function fmtTs(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch { return ts; }
}

function FilialBadge({ filial }) {
  const cores = { Vitalcare: { bg:'#eff6ff', color:'#1d4ed8', border:'#bfdbfe' }, Homebaby: { bg:'#fdf4ff', color:'#7e22ce', border:'#e9d5ff' } };
  const normalizado = filial === 'Homesenior' ? 'Vitalcare' : filial;
  const s = cores[normalizado] || { bg:'#f1f5f9', color:'#475569', border:'#cbd5e1' };
  return <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 7px', borderRadius:5, border:`1px solid ${s.border}`, background:s.bg, color:s.color }}>{normalizado || 'Homecare'}</span>;
}

function PadResumo({ pad }) {
  if (!pad) return <span style={{ fontSize:'0.75rem', color:C.muted, fontStyle:'italic' }}>PAD não informado</span>;
  const ativos = PAD_DISCIPLINES.filter(d => pad[d.key]?.enabled);
  if (!ativos.length) return <span style={{ fontSize:'0.75rem', color:C.muted, fontStyle:'italic' }}>Nenhuma disciplina ativa</span>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
      {ativos.map(d => {
        const item = pad[d.key];
        let freq = item.frequencia;
        if (!freq && item.subspecialidades) {
          const subs = Object.entries(item.subspecialidades).filter(([, v]) => v && v !== 'N/A').map(([s, f]) => `${s}: ${f}`);
          freq = subs.join(', ') || '—';
        }
        return (
          <div key={d.key} style={{ display:'flex', gap:6, fontSize:'0.78rem' }}>
            <span style={{ fontWeight:600, color:C.text, minWidth:100 }}>{d.label}</span>
            <span style={{ color:C.muted }}>{freq || '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

function ItemBadge({ ativo, label, icon }) {
  if (!ativo) return null;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:'0.7rem', fontWeight:600, padding:'2px 8px', borderRadius:5, background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0' }}>
      {icon} {label}
    </span>
  );
}

function PatientCard({ paciente, onChecar, checando }) {
  const [expanded, setExpanded] = useState(false);
  const implDados  = paciente.dadosImplantacao || {};
  const pad        = paciente.dadosPAD || paciente.dadosImplantacao?.pad;
  const historico  = paciente.historico || [];
  const implEntry  = [...historico].reverse().find(h => h.status === 'Implantado');
  const emImplEntry = [...historico].reverse().find(h => h.status === 'Em Implantação');
  const obs        = implEntry?.dados?.obs || emImplEntry?.dados?.obs || '';
  const dataImpl   = paciente.dataImplantacaoPrevista || implDados.dataImplantacao || '';
  const horaImpl   = paciente.horaImplantacaoPrevista || implDados.horaImplantacao || '';

  return (
    <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, boxShadow:'0 1px 6px rgba(0,0,0,0.06)', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', borderBottom:`1px solid ${C.border}`, background:C.greenBg }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:6 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.65rem', fontWeight:700, color:C.muted, letterSpacing:'0.05em' }}>{paciente.numero}</div>
            <div style={{ fontWeight:800, fontSize:'0.95rem', color:C.text, marginTop:1 }}>{paciente.nome}</div>
          </div>
          <span style={{ fontSize:'0.75rem', fontWeight:700, padding:'3px 8px', borderRadius:6, background:'#dcfce7', color:'#15803d', border:'1px solid #86efac', whiteSpace:'nowrap' }}>✅ Implantado</span>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
          <FilialBadge filial={paciente.filial} />
          {paciente.convenio && <span style={{ fontSize:'0.7rem', color:C.muted }}>{paciente.convenio}</span>}
          {paciente.categoria && <span style={{ fontSize:'0.7rem', fontWeight:600, padding:'2px 7px', borderRadius:5, background:'#e0f2fe', color:'#0369a1', border:'1px solid #bae6fd' }}>{paciente.categoria}</span>}
          {paciente.classificacao && <span style={{ fontSize:'0.7rem', fontWeight:600, padding:'2px 7px', borderRadius:5, background:'#fef9c3', color:'#854d0e', border:'1px solid #fde68a' }}>{paciente.classificacao}</span>}
          {paciente.vm && <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 7px', borderRadius:5, background:'#fee2e2', color:'#dc2626', border:'1px solid #fecaca' }}>VM</span>}
        </div>
        {dataImpl && (
          <div style={{ marginTop:8, fontSize:'0.78rem', color:'#065f46', fontWeight:600 }}>
            📅 Implantação: {fmtDate(dataImpl)}{horaImpl ? ` às ${horaImpl}` : ''}
          </div>
        )}
      </div>

      <div style={{ padding:'10px 16px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ fontSize:'0.65rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>📋 PAD</div>
        <PadResumo pad={pad} />
      </div>

      <div style={{ padding:'8px 16px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          <ItemBadge ativo={implDados.suprimentos}  label="Suprimentos"  icon="📦" />
          <ItemBadge ativo={implDados.medicamentos} label="Medicamentos" icon="💊" />
          <ItemBadge ativo={implDados.material}     label="Material"     icon="🔧" />
          <ItemBadge ativo={implDados.mobiliario}   label="Mobiliário"   icon="🛋" />
          {implDados.tipoRemocao && implDados.tipoRemocao !== 'Simples' && (
            <span style={{ fontSize:'0.7rem', fontWeight:600, padding:'2px 8px', borderRadius:5, background:'#f5f3ff', color:'#5b21b6', border:'1px solid #ddd6fe' }}>
              🚗 {implDados.tipoRemocao}
            </span>
          )}
          {!implDados.suprimentos && !implDados.medicamentos && !implDados.material && !implDados.mobiliario && !implDados.tipoRemocao && (
            <span style={{ fontSize:'0.72rem', color:C.muted, fontStyle:'italic' }}>Sem itens especiais</span>
          )}
        </div>
      </div>

      {obs && (
        <div style={{ padding:'8px 16px', borderBottom:`1px solid ${C.border}`, background:'#fffbeb' }}>
          <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#a16207', textTransform:'uppercase' }}>Obs: </span>
          <span style={{ fontSize:'0.78rem', color:'#92400e' }}>{obs}</span>
        </div>
      )}

      <div style={{ padding:'4px 16px', borderBottom:`1px solid ${C.border}` }}>
        <button onClick={() => setExpanded(v => !v)}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.72rem', color:C.primary, fontWeight:600, padding:'4px 0' }}>
          {expanded ? '▲ Ocultar detalhes' : '▼ Ver mais'}
        </button>
        {expanded && (
          <div style={{ paddingBottom:8, display:'flex', flexDirection:'column', gap:4 }}>
            {[
              { l:'Hospital',          v: paciente.hospital },
              { l:'Região',            v: paciente.regiaoHospital },
              { l:'CEP',               v: paciente.cep },
              { l:'Tipo Implantação',  v: paciente.tipoImplantacao },
              { l:'Telefone',          v: paciente.telefone },
              { l:'Data Nasc.',        v: paciente.dataNascimento ? fmtDate(paciente.dataNascimento) : null },
              { l:'Liminar',           v: paciente.liminar ? 'Sim' : null },
            ].filter(i => i.v).map(i => (
              <div key={i.l} style={{ display:'flex', gap:6, fontSize:'0.76rem' }}>
                <span style={{ color:C.muted, minWidth:110 }}>{i.l}:</span>
                <span style={{ color:C.text, fontWeight:600 }}>{i.v}</span>
              </div>
            ))}
            {paciente.observacoes && (
              <div style={{ marginTop:4, fontSize:'0.76rem', color:C.text }}>
                <span style={{ color:C.muted }}>Obs gerais: </span>{paciente.observacoes}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding:'10px 16px' }}>
        <button
          onClick={() => onChecar(paciente)}
          disabled={checando}
          style={{ width:'100%', padding:'9px', borderRadius:8, border:'none', background:C.primary, color:'#fff', fontWeight:700, fontSize:'0.85rem', cursor:checando ? 'wait' : 'pointer', opacity:checando ? 0.7 : 1 }}>
          {checando ? '⏳ Salvando...' : '✓ Checar — Supervisor Enf. Núcleo'}
        </button>
      </div>
    </div>
  );
}

function CheckedCard({ paciente, currentEmail }) {
  const [expanded, setExpanded] = useState(false);
  // Meu check: novo mapa por usuário, com fallback para campo legado
  const ch = paciente.supervisorChecous?.[currentEmail] || (paciente.supervisorChecou?.por === currentEmail ? paciente.supervisorChecou : null) || {};
  const totalChecados = Object.keys(paciente.supervisorChecous || {}).length + (paciente.supervisorChecou && !paciente.supervisorChecous ? 1 : 0);
  const pad = paciente.dadosPAD || paciente.dadosImplantacao?.pad;
  const implDados = paciente.dadosImplantacao || {};
  const dataImpl  = paciente.dataImplantacaoPrevista || implDados.dataImplantacao || '';
  const horaImpl  = paciente.horaImplantacaoPrevista || implDados.horaImplantacao || '';

  return (
    <div style={{ background:'#fafbfc', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden', opacity:0.92 }}>
      <div style={{ padding:'12px 16px 8px' }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:4 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'0.65rem', color:C.muted }}>{paciente.numero}</div>
            <div style={{ fontWeight:700, fontSize:'0.9rem', color:C.text }}>{paciente.nome}</div>
          </div>
          <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:5, background:'#dcfce7', color:'#16a34a', border:'1px solid #86efac', whiteSpace:'nowrap' }}>✅ Checado</span>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:6 }}>
          <FilialBadge filial={paciente.filial} />
          {paciente.convenio && <span style={{ fontSize:'0.7rem', color:C.muted }}>{paciente.convenio}</span>}
          {paciente.categoria && <span style={{ fontSize:'0.7rem', fontWeight:600, padding:'2px 6px', borderRadius:5, background:'#e0f2fe', color:'#0369a1', border:'1px solid #bae6fd' }}>{paciente.categoria}</span>}
        </div>
        {dataImpl && <div style={{ fontSize:'0.75rem', color:C.muted }}>📅 {fmtDate(dataImpl)}{horaImpl ? ` às ${horaImpl}` : ''}</div>}
        <div style={{ marginTop:6, padding:'5px 10px', background:'#f0fdf4', borderRadius:7, border:'1px solid #bbf7d0' }}>
          <div style={{ fontSize:'0.68rem', color:'#166534' }}>
            <strong>Meu check:</strong> {fmtTs(ch.em) || '—'}
          </div>
          {totalChecados > 1 && (
            <div style={{ fontSize:'0.65rem', color:'#4ade80', marginTop:2 }}>
              {totalChecados} supervisores já checaram este paciente
            </div>
          )}
        </div>
        <button onClick={() => setExpanded(v => !v)}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.7rem', color:C.muted, padding:'4px 0', marginTop:4 }}>
          {expanded ? '▲ Ocultar' : '▼ Ver PAD / Detalhes'}
        </button>
        {expanded && (
          <div style={{ marginTop:4 }}>
            <PadResumo pad={pad} />
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
              <ItemBadge ativo={implDados.suprimentos}  label="Suprimentos"  icon="📦" />
              <ItemBadge ativo={implDados.medicamentos} label="Medicamentos" icon="💊" />
              <ItemBadge ativo={implDados.material}     label="Material"     icon="🔧" />
              <ItemBadge ativo={implDados.mobiliario}   label="Mobiliário"   icon="🛋" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const PAINEL_DESATIVADO = true;

export default function PainelImplantados() {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('aguardando');
  const [filtroOp, setFiltroOp]   = useState('');
  const [checando, setChecando]   = useState(null);
  const user = auth.currentUser;
  const currentEmail = user?.email || '';

  useEffect(() => {
    if (PAINEL_DESATIVADO) return; // não assina o listener pesado enquanto desativado
    const q = query(
      collection(db, getCollectionName('captacao_pacientes')),
      where('status', '==', 'Implantado'),
      orderBy('dataAtualizacao', 'desc'),
      resumo('captacao_resumo') // sem as cópias do formulário de áreas
    );
    const unsub = onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPacientes(all.filter(p => p.status === 'Implantado' && !p.excluido));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  const operadoras = useMemo(() => [...new Set(pacientes.map(p => p.convenio).filter(Boolean))].sort(), [pacientes]);

  const filtrados = useMemo(() => {
    return pacientes.filter(p => !filtroOp || p.convenio === filtroOp);
  }, [pacientes, filtroOp]);

  if (PAINEL_DESATIVADO) {
    return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
        <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Painel temporariamente desativado</h3>
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Em manutenção. Volta em breve.</p>
      </div>
    );
  }

  // Checado POR MIM = meu email está em supervisorChecous OU legado supervisorChecou.por === meu email
  const euChecei = (p) =>
    !!(p.supervisorChecous?.[currentEmail]) ||
    (!!p.supervisorChecou && !p.supervisorChecous && p.supervisorChecou.por === currentEmail);

  const aguardando = filtrados.filter(p => !euChecei(p));
  const checados   = filtrados.filter(p =>  euChecei(p));

  const handleChecar = async (paciente) => {
    setChecando(paciente.id);
    try {
      const email = currentEmail || 'supervisor';
      // Preserva checks de outros usuários, adiciona/atualiza o meu
      const checousAtual = paciente.supervisorChecous || {};
      await updateDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id), {
        supervisorChecous: { ...checousAtual, [email]: { em: new Date().toISOString(), por: email } },
        supervisorChecadoEm: serverTimestamp(),
      });
    } catch (e) {
      alert('Erro ao checar: ' + e.message);
    } finally {
      setChecando(null);
    }
  };

  return (
    <div style={{ padding:'28px 28px 40px', background:'#f8fafc', minHeight:'100vh' }}>
      <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ margin:'0 0 4px', fontSize:'1.3rem', fontWeight:800, color:C.text }}>✅ Painel de Implantados</h2>
          <p style={{ margin:0, color:C.muted, fontSize:'0.83rem' }}>Supervisão de enfermagem — checagem de pacientes implantados</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <div style={{ textAlign:'center', padding:'8px 16px', borderRadius:10, background:'#fff', border:'1px solid #e2e8f0' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:800, color:C.primary }}>{aguardando.length}</div>
            <div style={{ fontSize:'0.68rem', color:C.muted, textTransform:'uppercase', fontWeight:600 }}>A checar</div>
          </div>
          <div style={{ textAlign:'center', padding:'8px 16px', borderRadius:10, background:'#fff', border:'1px solid #e2e8f0' }}>
            <div style={{ fontSize:'1.4rem', fontWeight:800, color:'#22c55e' }}>{checados.length}</div>
            <div style={{ fontSize:'0.68rem', color:C.muted, textTransform:'uppercase', fontWeight:600 }}>Checados</div>
          </div>
        </div>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'center', flexWrap:'wrap' }}>
        {[
          { k:'aguardando', l:`⏳ Aguardando Checagem (${aguardando.length})` },
          { k:'checados',   l:`✅ Checados (${checados.length})` },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:'0.84rem',
              background: tab === t.k ? C.primary : '#fff',
              color:       tab === t.k ? '#fff'     : C.muted,
              boxShadow:   tab === t.k ? `0 2px 8px ${C.primary}40` : '0 1px 3px rgba(0,0,0,0.08)',
              border: tab !== t.k ? `1px solid ${C.border}` : 'none',
            }}>
            {t.l}
          </button>
        ))}
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:'0.78rem', color:C.muted, fontWeight:600 }}>Operadora:</span>
          <select value={filtroOp} onChange={e => setFiltroOp(e.target.value)}
            style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 10px', fontSize:'0.82rem', background:'#fff', cursor:'pointer' }}>
            <option value="">Todas</option>
            {operadoras.map(op => <option key={op} value={op}>{op}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:C.muted }}>⏳ Carregando...</div>
      ) : tab === 'aguardando' ? (
        aguardando.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:C.muted }}>
            <div style={{ fontSize:'3rem', marginBottom:12 }}>🎉</div>
            <p style={{ margin:0, fontWeight:600, fontSize:'1rem' }}>Todos os pacientes já foram checados!</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:16 }}>
            {aguardando.map(p => (
              <PatientCard key={p.id} paciente={p} onChecar={handleChecar} checando={checando === p.id} />
            ))}
          </div>
        )
      ) : (
        checados.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:C.muted }}>
            <div style={{ fontSize:'2.5rem', marginBottom:12 }}>📋</div>
            <p style={{ margin:0, fontWeight:600 }}>Nenhum paciente checado ainda.</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
            {checados.map(p => <CheckedCard key={p.id} paciente={p} currentEmail={currentEmail} />)}
          </div>
        )
      )}
    </div>
  );
}
